import _, {find} from 'lodash';
import {batch} from 'react-redux';
import {setAllPartOfTipTouchOffTask} from 'lib/PipettingProfileItemHelper';
import {toTextPromise} from 'lib/fileHelper';
import {getCardIdsByGroupId} from 'lib/cardAndTaskHelper';
import {
  SignalREvent,
  SignalRHub,
  FastRobotErrorcode,
  ProtocolEditorViewMode,
  TaskType,
  CardType,
  SubCardType,
  ModalSize,
  TrayType,
  CSVImportPlateColumnPreference,
} from '../../constants/dataEnum';
import {addTemporaryState, removeTemporaryState} from '../actions/temporaryStateAction';
import {setErrorCodeNumber, invokeAPI, resolveIntl} from './commonBusiness';
import {saveState} from './stateBusiness';
import {showLoading, hideLoading} from '../actions/appStateAction';
import {showModal, hideModal} from '../actions/modalStateAction';
import {convertCsvToCardState} from './protocolCSVConversionBusiness';
import {restoreCardState, restoreCardTaskState} from '../actions/cardStateAction';
import {removeLabware, renameLabware, resetLabwareState, restoreLabwareState} from '../actions/labwareStateAction';
import {setSelectedMenu} from '../actions/protocolEditorAction';
import {findKeyByValue} from '../../lib/objectHelper';
import {globalAddUndoItem} from './undoRedoBusinessAction';
import {generateCardIDAndTaskIDAndNumber} from './commonTaskListBusiness';
import {checkInvalidTask, smarterDefaultDispenseTipPosition} from './taskListBusinessAction';
import jsonCopy from '../../lib/jsonCopy';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {restoreTipCountForCards} from './protocolManagementBusiness';

const intl = resolveIntl();

export const TEMPORARY_STATE_PICKLIST = 'picklistDefinition';

export const getPicklistLabwareIDs =
  (allButIgnoredCard = false) =>
  (dispatch, getState) => {
    const {cardState} = getState();
    const {cards, tasks} = cardState;

    const picklistLabwareIDs = [];

    let picklistCards;
    if (allButIgnoredCard) {
      picklistCards = Object.values(cards).filter((card) => card.subType === SubCardType.PICKLIST && !card?.isIgnored);
    } else {
      picklistCards = Object.values(cards).filter((card) => card.subType === SubCardType.PICKLIST);
    }

    picklistCards.forEach((card) => {
      card.tasks.forEach((taskID) => {
        const task = tasks[taskID];
        if (
          (task.type === TaskType.ASPIRATE || task.type === TaskType.DISPENSE) &&
          task.labwareID &&
          !picklistLabwareIDs.includes(task.labwareID)
        ) {
          picklistLabwareIDs.push(task.labwareID);
        }
      });
    });

    return picklistLabwareIDs;
  };

export const saveLabwareStateBeforePicklist = () => (dispatch, getState) => {
  const {labwareState} = getState();

  dispatch(addTemporaryState('labwareStateBeforePicklist', {labwareState}));
};

export const restoreLabwareStateAfterCancelPicklist = () => (dispatch, getState) => {
  const {temporaryState} = getState();
  const {labwareState} = temporaryState.labwareStateBeforePicklist;

  dispatch(removeTemporaryState('labwareStateBeforePicklist'));
  dispatch(restoreLabwareState(labwareState));
};

export const updatePicklist =
  (newPicklistDefinition, skipSaveState = false) =>
  (dispatch) => {
    batch(() => {
      dispatch(addTemporaryState(TEMPORARY_STATE_PICKLIST, newPicklistDefinition));
      if (!skipSaveState) {
        dispatch(saveState());
      }
    });
  };

export const clearPickList =
  (skipSaveState = false) =>
  (dispatch) => {
    batch(() => {
      dispatch(removeTemporaryState(TEMPORARY_STATE_PICKLIST));
      if (!skipSaveState) {
        dispatch(saveState());
      }
    });
  };

export const cancelPicklist =
  (skipSaveState = false) =>
  (dispatch) => {
    batch(() => {
      dispatch(removeTemporaryState(TEMPORARY_STATE_PICKLIST));
      dispatch(resetLabwareState());
      dispatch(restoreLabwareStateAfterCancelPicklist());
      dispatch(setSelectedMenu(ProtocolEditorViewMode.SIMPLE_TRANSFER));
      if (!skipSaveState) {
        dispatch(saveState());
      }
    });
  };

export const getPicklistDefinition = () => (dispatch, getState) => {
  const {temporaryState} = getState();
  const {picklistDefinition} = temporaryState;

  return picklistDefinition ?? {labwareMap: {}, currentStep: null, csvFile: ''};
};

export const setPicklistOriginalLabware = (originalLabware) => (dispatch, getState) => {
  const {labwareState} = getState();
  const labwareToSave = originalLabware ?? labwareState;
  const picklistDefinition = dispatch(getPicklistDefinition());

  const newPickDef = {
    ...picklistDefinition,
    originalLabware: jsonCopy(labwareToSave),
  };

  dispatch(updatePicklist(newPickDef));
};

export const getPicklistOriginalLabware = () => (dispatch) => {
  const picklistDefinition = dispatch(getPicklistDefinition());
  return picklistDefinition.originalLabware;
};

export const setPicklistDeckId = (stepId, deckId) => (dispatch) => {
  const picklistDefinition = dispatch(getPicklistDefinition());

  if (!(stepId in picklistDefinition.labwareMap)) {
    return;
  }

  const newPickDef = {
    ...picklistDefinition,
    labwareMap: {
      ...picklistDefinition.labwareMap,
      [stepId]: {
        ...picklistDefinition.labwareMap[stepId],
        deckId,
      },
    },
  };

  dispatch(updatePicklist(newPickDef));
};

export const unSetPicklistDeckId = (stepId) => (dispatch) => {
  const picklistDefinition = dispatch(getPicklistDefinition());

  if (!(stepId in picklistDefinition.labwareMap)) {
    return;
  }

  const newPickDef = {
    ...picklistDefinition,
    labwareMap: {
      ...picklistDefinition.labwareMap,
      [stepId]: {
        ...picklistDefinition.labwareMap[stepId],
        deckId: undefined,
      },
    },
  };

  dispatch(updatePicklist(newPickDef));
};

export const swapPicklistDeckId =
  (targetDeckId, sourceDeckId, skipAddUndoItem = false) =>
  (dispatch) => {
    const picklistDefinition = dispatch(getPicklistDefinition());
    const {labwareMap} = picklistDefinition;

    const targetObj = find(labwareMap, {deckId: targetDeckId});
    const sourceObj = find(labwareMap, {deckId: sourceDeckId});
    const newPickDef = {
      ...picklistDefinition,
    };

    const originalLabware = dispatch(getPicklistOriginalLabware());
    const temp = jsonCopy(originalLabware[targetDeckId]);
    originalLabware[targetDeckId] = jsonCopy(originalLabware[sourceDeckId]);
    originalLabware[sourceDeckId] = temp;
    newPickDef.originalLabware = originalLabware;

    if (targetObj?.id) {
      newPickDef.labwareMap[targetObj.id].deckId = sourceDeckId;
    }

    if (sourceObj?.id) {
      newPickDef.labwareMap[sourceObj.id].deckId = targetDeckId;
    }

    dispatch(updatePicklist(newPickDef));
    if (!skipAddUndoItem) {
      dispatch(globalAddUndoItem());
    }
    return true;
  };

export const incrementCurrentStep = () => (dispatch) => {
  const picklistDefinition = dispatch(getPicklistDefinition());

  const maxStep = Object.keys(picklistDefinition.labwareMap).length;
  const currentStep = picklistDefinition.currentStep + 1 > maxStep ? maxStep : picklistDefinition.currentStep + 1;

  const newPickDef = {
    ...picklistDefinition,
    currentStep,
  };
  dispatch(updatePicklist(newPickDef));
  return currentStep;
};

export const decrementCurrentStep = () => (dispatch) => {
  const picklistDefinition = dispatch(getPicklistDefinition());

  let {labwareMap} = picklistDefinition;
  const currentLabwareMap = labwareMap[picklistDefinition.currentStep];
  const {deckId} = currentLabwareMap;

  // reset deck Id
  currentLabwareMap.deckId = undefined;
  labwareMap = {...labwareMap, [currentLabwareMap.id]: currentLabwareMap};

  const currentStep = picklistDefinition.currentStep - 1 < 1 ? 1 : picklistDefinition.currentStep - 1;

  const newPickDef = {
    ...picklistDefinition,
    labwareMap,
    currentStep,
  };

  dispatch(removeLabware(deckId));
  dispatch(updatePicklist(newPickDef));
  return currentStep;
};

export const getIdentifiedCsvVersion = (csv) => async (dispatch, getState) => {
  const {protocolState} = getState();
  const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

  const response = await dispatch(
    invokeAPI(SignalRHub.AUTHORING, SignalREvent.IDENTIFY_CSV_VERSION, csv, protocolTrayType),
  );
  console.log(`${SignalREvent.IDENTIFY_CSV_VERSION} response: `, response);
  return response;
};

export const getPicklistUsedPlateIdentification = (csvFile) => async (dispatch, getState) => {
  const {protocolState} = getState();
  const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

  const response = await dispatch(
    invokeAPI(SignalRHub.AUTHORING, SignalREvent.IDENTIFY_USED_PLATE_IN_PICKLIST, csvFile, protocolTrayType),
  );
  console.log(`${SignalREvent.IDENTIFY_USED_PLATE_IN_PICKLIST} response: `, response);
  return response;
};

export const getPicklistToProtocolConvertion = () => async (dispatch, getState) => {
  const {labwareState, protocolState} = getState();
  const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;
  const protocolEjectTipLocation = protocolState.lastOpenedProtocol?.isEjectToCaddy ? 1 : 0;
  const picklistDefinition = dispatch(getPicklistDefinition());
  const {labwareMap, csvFile, platePreference} = picklistDefinition;
  const plateDefinition = {};
  Object.keys(labwareMap).forEach((idx) => {
    const labware = labwareMap[idx];
    plateDefinition[labware?.name] = {
      PlateID: labwareState[labware?.deckId]?.IDPrefix,
      Position: labware?.deckId,
      Barcode: labware?.barcode,
    };
  });

  const response = await dispatch(
    invokeAPI(
      SignalRHub.AUTHORING,
      SignalREvent.CONVERT_PICKLIST_TO_PROTOCOL,
      csvFile,
      plateDefinition,
      protocolTrayType,
      protocolEjectTipLocation,
      platePreference,
    ),
  );
  console.log(`${SignalREvent.CONVERT_PICKLIST_TO_PROTOCOL} response: `, response);
  return response;
};

const showErrorConversionModal = (errorCode) => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'protocolEditor.picklist.fail.title',
      }),
      <>
        <div className="error-subtitle">
          <span>{intl.formatMessage({id: 'protocolEditor.picklist.fail.failedToConvert'})}</span>
        </div>
        <span style={{fontWeight: 'normal'}}>
          {intl.formatMessage({id: 'protocolEditor.picklist.fail.failedToConvertDetail'}, {errorCode})}
        </span>
      </>,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            dispatch(hideModal());
            dispatch(cancelPicklist(true));
          },
          text: intl.formatMessage({
            id: 'protocolEditor.picklist.fail.firstButton',
          }),
        },
      ],
      () => {},
      'upload-fail-modal',
      true,
      () => dispatch(hideModal()),
    ),
  );
};

export const normalizeEjectTip = (tasks) => {
  let lastPickTipTask = null;

  Object.keys(tasks).forEach((taskKey) => {
    const task = tasks[taskKey];
    if (task.type === TaskType.PICK_TIP) {
      lastPickTipTask = task;
    }

    if (task.type === TaskType.EJECT_TIP && lastPickTipTask) {
      // change/mutate task rowTipCount and columnTipCount based on related pickTip
      task.rowTipCount = lastPickTipTask.rowTipCount ?? 0;
      task.columnTipCount = lastPickTipTask.columnTipCount ?? 0;

      lastPickTipTask = null;
    }
  });
};

export const getUpdatedLabwareStateAfterCsvImport = (originalLabwareState, csvImportLabwareState) => {
  const newLabwareStateValue = Object.keys(originalLabwareState).reduce((prev, idxKey) => {
    const originalLabware = originalLabwareState[idxKey];
    const csvImportLabware = csvImportLabwareState[idxKey];

    let currentLabware;

    if (csvImportLabware.LabwareID) {
      const isNeedToSetBarcodeValue = Boolean(!csvImportLabware.barcode && originalLabware.barcode);

      currentLabware = {
        ...csvImportLabware,
        ...(isNeedToSetBarcodeValue ? {barcode: originalLabware.barcode} : {}),
        // keep the isRenamed state always true
        isRenamed: true,
      };
    } else {
      currentLabware = {...originalLabware};
    }

    prev[idxKey] = currentLabware;
    return prev;
  }, {});

  return newLabwareStateValue;
};

export const finalizePicklist =
  (
    // eslint-disable-next-line default-param-last
    skipSaveState = false,
    addCardGroup,
  ) =>
  async (dispatch, getState) => {
    const {availableLabwareState, cardState: oldCardState, labwareState: oldLabwareState, protocolState} = getState();
    const {cards: oldCards, tasks: oldTasks} = oldCardState;
    dispatch(
      showLoading(
        intl.formatMessage({
          id: 'about.updaterLoading.uploadFile.text2',
        }),
      ),
    );
    const result = await dispatch(getPicklistToProtocolConvertion());
    if (!result) {
      return;
    }

    const {errorCode, payload} = result;
    if (errorCode !== FastRobotErrorcode.NoError) {
      dispatch(hideModal());
      dispatch(showErrorConversionModal(errorCode));
      dispatch(hideLoading());
      if (!skipSaveState) {
        dispatch(saveState());
      }
      return;
    }

    const [cardId, taskId] = generateCardIDAndTaskIDAndNumber(
      oldCards,
      oldTasks,
      CardType.SIMPLE_TRANSFER,
      null,
      TaskType.ASPIRATE,
      null,
      {row: null, column: null},
    );

    const newState = convertCsvToCardState(payload, availableLabwareState, null, {
      startingCardId: cardId,
      startingTaskId: taskId,
      oldLabwareState,
    });

    const {cardState: csvImportCardState, labwareState: csvImportLabwareState} = newState;

    const {cards, tasks} = csvImportCardState;
    const updatedTasks = setAllPartOfTipTouchOffTask(tasks, csvImportLabwareState);
    const cardIds = Object.values(cards).map((card) => card.ID);

    normalizeEjectTip(updatedTasks);

    const dispenseLabwareIds = _.uniq(
      Object.values(updatedTasks)
        .filter((task) => task.type === TaskType.DISPENSE)
        .map((task) => task.labwareID),
    );

    const updatedCsvImportLabwareState = getUpdatedLabwareStateAfterCsvImport(oldLabwareState, csvImportLabwareState);

    const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;
    const isHighVolumeTray = protocolTrayType === TrayType.HIGH_VOLUME_CADDY;

    batch(() => {
      dispatch(restoreCardState({...oldCards, ...cards}));
      dispatch(restoreCardTaskState({...oldTasks, ...updatedTasks}));
      dispatch(restoreLabwareState(updatedCsvImportLabwareState));
      dispatch(clearPickList(true));
      const newCardGroup = addCardGroup(cardIds, cardIds[0], null, true, true, 'CSV Import');

      // workaround due to side-effect in addCardGroup function, get updated card ids after addCardGroup process
      const updatedCardIds = getCardIdsByGroupId(getState().cardState.cards, newCardGroup.ID);

      dispatch(smarterDefaultDispenseTipPosition(dispenseLabwareIds));

      updatedCardIds.forEach((cardId) => {
        dispatch(checkInvalidTask(cardId, false, true, true, false, isHighVolumeTray));
      });

      dispatch(restoreTipCountForCards(updatedCardIds));

      dispatch(hideLoading());
      return true;
    });
  };

export const getCSVErrorInfoDetail = (fastErrorCode) => (dispatch, getState) => {
  const {configState} = getState();
  const {maxAspirateHighVolume, maxAspirateVolume, minAspirateHighVolume, minAspirateVolume} = configState;
  let errorSubTitle;
  let errorMessage;

  switch (fastErrorCode) {
    case FastRobotErrorcode.TOO_MANY_LABWARES: {
      const {protocolState} = getState();
      const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

      errorSubTitle = intl.formatMessage(
        {id: 'protocolEditor.picklist.fail.tooManyLabware'},
        {labwareCount: protocolTrayType === TrayType.DOUBLE_CADDY ? 4 : 5},
      );
      errorMessage = intl.formatMessage(
        {id: 'protocolEditor.picklist.fail.tooManyLabwareDetail'},
        {totalLabware: fastErrorCode},
      );
      break;
    }
    case FastRobotErrorcode.UNSUPPORTED_LABWARE:
      errorSubTitle = intl.formatMessage({id: 'protocolEditor.picklist.fail.unsupportedLabware'});
      errorMessage = intl.formatMessage({id: 'protocolEditor.picklist.fail.unsupportedLabwareDetail'});
      break;
    case FastRobotErrorcode.UNSUPPORTED_VOLUME_TRANSFER: {
      const minVolumeInNano = minAspirateVolume.value * 1000;
      const minHighVolumeInNano = minAspirateHighVolume.value * 1000;

      errorSubTitle = intl.formatMessage({id: 'protocolEditor.picklist.fail.unsupportedVolumeTransfer'});
      errorMessage = intl.formatMessage(
        {id: 'protocolEditor.picklist.fail.unsupportedVolumeTransferDetail'},
        {
          minAspirateHighVolume: minHighVolumeInNano,
          minAspirateVolume: minVolumeInNano,
        },
      );
      break;
    }
    case FastRobotErrorcode.INVALID_WELL_TARGET:
      errorSubTitle = intl.formatMessage({id: 'protocolEditor.picklist.fail.invalidWellTarget'});
      errorMessage = intl.formatMessage({id: 'protocolEditor.picklist.fail.invalidWellTargetDetail'});
      break;
    case FastRobotErrorcode.CSV_MISSING_REQUIRED_FIELD:
      errorSubTitle = intl.formatMessage({id: 'protocolEditor.picklist.fail.csvMissingRequiredField'});
      break;
    case FastRobotErrorcode.INVALID_CSV_ROW:
      errorSubTitle = intl.formatMessage({id: 'protocolEditor.picklist.fail.invalidCsvRow'});
      break;
    case FastRobotErrorcode.INVALID_CSV_DATA_TYPE:
      errorSubTitle = intl.formatMessage({id: 'protocolEditor.picklist.fail.invalidCsvDataType'});
      errorMessage = intl.formatMessage({id: 'protocolEditor.picklist.fail.invalidCsvDataTypeDetail'});
      break;
    case FastRobotErrorcode.INVALID_CSV_IMPORT_MIXING_VOLUME: {
      const maxVolumeInNano = maxAspirateVolume.value * 1000;
      const maxHighVolumeInNano = maxAspirateHighVolume.value * 1000;

      errorSubTitle = intl.formatMessage({id: 'protocolEditor.picklist.fail.invalidCsvImportMixingVolume'});
      errorMessage = intl.formatMessage(
        {id: 'protocolEditor.picklist.fail.invalidCsvImportMixingVolumeDetail'},
        {maxAspirateVolume: maxVolumeInNano, maxAspirateHighVolume: maxHighVolumeInNano},
      );
      break;
    }
    default:
      errorSubTitle = intl.formatMessage({id: 'protocolEditor.picklist.fail.invalidFile'});
      errorMessage = intl.formatMessage({id: 'protocolEditor.picklist.fail.invalidFileDetail'});
      break;
  }

  return {errorSubTitle, errorMessage};
};

// TODO: REMOVE!!!!!!!!!
/**
 * @deprecated
 * replaced with getCSVErrorInfoDetail
 */
const showFailedUploadModal = (fastErrorCode, pickListUploadWindowRef) => async (dispatch, getState) => {
  let errorSubTitle;
  let errorMessage;
  let props = {};

  await dispatch(setErrorCodeNumber(fastErrorCode));

  switch (fastErrorCode) {
    case FastRobotErrorcode.TOO_MANY_LABWARES: {
      const {protocolState} = getState();
      const protocolTrayType = protocolState.lastOpenedProtocol?.trayType;

      errorSubTitle = 'protocolEditor.picklist.fail.tooManyLabware';
      errorMessage = intl.formatMessage(
        {id: 'protocolEditor.picklist.fail.tooManyLabwareDetail'},
        {totalLabware: fastErrorCode},
      );
      props = {labwareCount: protocolTrayType === TrayType.DOUBLE_CADDY ? 4 : 5};
      break;
    }
    case FastRobotErrorcode.UNSUPPORTED_VOLUME_TRANSFER:
      errorSubTitle = 'protocolEditor.picklist.fail.unsupportedVolume';
      errorMessage = intl.formatMessage({id: 'protocolEditor.picklist.fail.unsupportedVolumeDetail'});
      break;
    case FastRobotErrorcode.UNSUPPORTED_LABWARE:
      errorSubTitle = 'protocolEditor.picklist.fail.unsupportedLabware';
      errorMessage = intl.formatMessage({id: 'protocolEditor.picklist.fail.unsupportedLabwareDetail'});
      break;
    case FastRobotErrorcode.INVALID_WELL_TARGET:
      errorSubTitle = 'protocolEditor.picklist.fail.invalidWellTarget';
      errorMessage = intl.formatMessage({id: 'protocolEditor.picklist.fail.invalidWellTargetDetail'});
      break;
    default:
      errorSubTitle = 'protocolEditor.picklist.fail.invalidFile';
      errorMessage = intl.formatMessage({id: 'protocolEditor.picklist.fail.invalidFileDetail'});
      break;
  }
  dispatch(
    showModal(
      intl.formatMessage(
        {
          id: errorSubTitle,
        },
        props,
      ),
      errorMessage,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            pickListUploadWindowRef.current.open();
          },
          text: intl.formatMessage({
            id: 'protocolEditor.picklist.fail.firstButton',
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
          },
          text: intl.formatMessage({
            id: 'protocolEditor.picklist.fail.secondButton',
          }),
        },
      ],
      () => {},
      'upload-fail-modal',
      true,
      () => dispatch(hideModal()),
    ),
  );
};

// TODO: fix the naming
export const isValidCsvLabwareWithCurrentProtocol = (csvLabwares) => (dispatch, getState) => {
  const {labwareState} = getState();
  const csvLabwareCount = csvLabwares?.length;
  let availableDeck = 5;

  const picklistLabwareIDs = dispatch(getPicklistLabwareIDs());

  Object.keys(labwareState).forEach((key) => {
    const labware = labwareState[key];
    const labwareUsed = picklistLabwareIDs.includes(labware.LabwareID);
    const sameLabware = csvLabwares.find((newLabware) => newLabware.name === labware.barcode);

    if (key !== '2' && labwareUsed && !sameLabware) {
      availableDeck -= 1;
    }
  });

  // TODO: negate this condition
  return !(csvLabwareCount < 1 || csvLabwareCount > availableDeck);
};

export const processIdentifyCSVFile = (csvFile) => async (dispatch) => {
  const result = {
    csvStr: null,
    csvVersion: null,
    csvPlate: null,
    errorCode: FastRobotErrorcode.NoError,
    errorDetail: null,
    isValidAgainstProtocol: false,
  };

  const csvStr = await toTextPromise(csvFile);
  result.csvStr = csvStr;

  const csvVersionResponse = await dispatch(getIdentifiedCsvVersion(csvStr));
  if (csvVersionResponse.errorCode !== FastRobotErrorcode.NoError) {
    result.errorCode = csvVersionResponse.errorCode;
    result.errorDetail = csvVersionResponse.payload;
    return result;
  }
  result.csvVersion = csvVersionResponse.payload;

  const csvPlateResponse = await dispatch(getPicklistUsedPlateIdentification(csvStr));
  if (csvPlateResponse.errorCode !== FastRobotErrorcode.NoError) {
    result.errorCode = csvPlateResponse.errorCode;
    result.errorDetail = csvPlateResponse.payload;
    return result;
  }
  result.csvPlate = csvPlateResponse.payload;

  const isValidAgainstProtocol = dispatch(isValidCsvLabwareWithCurrentProtocol(result.csvPlate));
  result.isValidAgainstProtocol = isValidAgainstProtocol;

  return result;
};

// FIXME: consider better approach than this, should not mix get value of totalDeckIdNeedToDefine with preparation
// TODO: split the implementation of get totalDeckIdNeedToDefine and prepareCsvImport
export const prepareCsvImport =
  (csvStr, csvPlate, platePreference = CSVImportPlateColumnPreference.PLATE_BARCODE) =>
  (dispatch, getState) => {
    const {labwareState} = getState();

    dispatch(saveLabwareStateBeforePicklist());

    const labwareMap = {};
    let totalDeckIdNeedToDefine = 0;
    let idxReversed = csvPlate.length;
    csvPlate.forEach((labware) => {
      const barcode = labware?.name;
      // check is barcode already exist, if yes then move to last
      const existBarcode = findKeyByValue(labwareState, {barcode});
      if (existBarcode !== null && existBarcode !== undefined) {
        labwareMap[idxReversed] = {
          id: idxReversed,
          name: labware?.name,
          type: labware?.plateTypes,
          deckId: parseInt(existBarcode, 10),
          lastAssociatedPlateId: labware?.lastAssociatedPlateId,
          barcode: labware?.name,
        };
        idxReversed -= 1;
      } else {
        totalDeckIdNeedToDefine += 1;
        labwareMap[totalDeckIdNeedToDefine] = {
          id: totalDeckIdNeedToDefine,
          name: labware?.name,
          type: labware?.plateTypes,
          deckId: undefined,
          lastAssociatedPlateId: labware?.lastAssociatedPlateId,
          barcode: labware?.name,
        };
      }
    });

    dispatch(updatePicklist({labwareMap, currentStep: 1, csvFile: csvStr, totalDeckIdNeedToDefine, platePreference}));
    dispatch(setPicklistOriginalLabware());
    return totalDeckIdNeedToDefine;
  };

/**
 * @deprecated
 * splitted implementation with processIdentifyCSVFile and prepareCsvImport
 */
export const picklistImportProcess = (e, pickListUploadWindowRef) => async (dispatch, getState) => {
  const toText = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const {labwareState} = getState();

  if (!e.target.files.length) {
    return null;
  }

  const loadingMessage = intl.formatMessage({
    id: 'about.updaterLoading.uploadFile.text2',
  });
  dispatch(hideModal());
  dispatch(showLoading(loadingMessage));

  const file = e.target.files[0];
  const csvFile = await toText(file);

  // get plate type
  const response = await dispatch(getPicklistUsedPlateIdentification(csvFile));
  const {errorCode, payload} = response;

  if (errorCode !== FastRobotErrorcode.NoError) {
    dispatch(hideLoading());
    dispatch(showFailedUploadModal(errorCode, pickListUploadWindowRef));
    return null;
  }

  const picklistLabwareIDs = dispatch(getPicklistLabwareIDs());

  const totalLabware = payload.length;
  let availableDeck = 5;

  Object.keys(labwareState).forEach((key) => {
    const labware = labwareState[key];
    const labwareUsed = picklistLabwareIDs.includes(labware.LabwareID);
    const sameLabware = payload.find((newLabware) => newLabware.name === labware.barcode);

    if (key !== '2' && labwareUsed && !sameLabware) {
      availableDeck -= 1;
    }
  });

  if (totalLabware < 1 || totalLabware > availableDeck) {
    dispatch(hideLoading());
    dispatch(showFailedUploadModal(FastRobotErrorcode.INVALID_CONTENT, pickListUploadWindowRef));
    return null;
  }

  // TODO: check if this is needed
  dispatch(saveLabwareStateBeforePicklist());

  // setProtocolEditorMode(ProtocolEditorViewMode.PICKLIST);
  const labwareMap = {};
  let totalDeckIdNeedToDefine = 0;
  let idxReversed = payload.length;
  payload.forEach((labware) => {
    const barcode = labware?.name;
    // check is barcode already exist, if yes then move to last
    const existBarcode = findKeyByValue(labwareState, {barcode});
    if (existBarcode !== null && existBarcode !== undefined) {
      labwareMap[idxReversed] = {
        id: idxReversed,
        name: labware?.name,
        type: labware?.plateTypes,
        deckId: parseInt(existBarcode, 10),
        lastAssociatedPlateId: labware?.lastAssociatedPlateId,
        barcode: labware?.name,
      };
      idxReversed -= 1;
    } else {
      totalDeckIdNeedToDefine += 1;
      labwareMap[totalDeckIdNeedToDefine] = {
        id: totalDeckIdNeedToDefine,
        name: labware?.name,
        type: labware?.plateTypes,
        deckId: undefined,
        lastAssociatedPlateId: labware?.lastAssociatedPlateId,
        barcode: labware?.name,
      };
    }
  });

  dispatch(updatePicklist({labwareMap, currentStep: 1, csvFile, totalDeckIdNeedToDefine}));
  dispatch(setPicklistOriginalLabware());
  dispatch(hideLoading());
  return totalDeckIdNeedToDefine;
};

export const updatePicklistOriginalLabware = (deckId, labwareDefinition) => (dispatch, getState) => {
  const {labwareState} = getState();
  const originalLabware = dispatch(getPicklistOriginalLabware());
  originalLabware[deckId] = labwareDefinition ?? labwareState[deckId];
  dispatch(setPicklistOriginalLabware(originalLabware));
};

export const picklistOnDroppedLabware = (deckId) => (dispatch) => {
  dispatch(updatePicklistOriginalLabware(deckId));
};

export const picklistOnBack = () => (dispatch) => {
  const picklistDefinition = dispatch(getPicklistDefinition());
  const {originalLabware} = picklistDefinition;

  const prevStep = picklistDefinition.currentStep - 1;
  dispatch(decrementCurrentStep());

  const previousDeckId = picklistDefinition.labwareMap[prevStep].deckId;
  const newName = originalLabware[previousDeckId].name;

  dispatch(renameLabware(previousDeckId, newName));
  dispatch(setPicklistDeckId(prevStep, undefined));
  dispatch(globalAddUndoItem());
};

export const removeUnusedPicklistBarcode =
  (skipAddUndoItem = false, isRestore = true) =>
  (dispatch, getState) => {
    const {labwareState} = getState();

    const picklistLabwareIDs = dispatch(getPicklistLabwareIDs(!isRestore));

    const newLabwareState = {};

    Object.keys(labwareState).forEach((key) => {
      newLabwareState[key] = jsonCopy(labwareState[key]);

      if (!picklistLabwareIDs.includes(newLabwareState[key].LabwareID)) {
        delete newLabwareState[key].barcode;
      }
    });
    if (isRestore) {
      dispatch(restoreLabwareState(newLabwareState));
    }
    if (!skipAddUndoItem) {
      dispatch(globalAddUndoItem());
    }
    return newLabwareState;
  };

// TODO: REMOVE!!!!!!!!!
/**
 * @deprecated
 */
// eslint-disable-next-line no-unused-vars
const generatePicklistCsvTemplate = () => {
  const PICK_LIST_TEMPALTE_HEADERS = [
    'Source Plate Name',
    'Source Well',
    'Destination Plate Name',
    'Destination Well',
    'Transfer Volume (nL)',
  ];

  const startSourceWell = 5;
  const startDestinationeWell = 6;
  const csv = [];
  const headers = [];

  PICK_LIST_TEMPALTE_HEADERS.forEach((header, idx) => {
    headers[idx] = `"${header}"`;
  });

  csv.push(headers.join(','));

  // generate dummy data
  for (let index = 0; index < 4; index += 1) {
    const plateName = 'Your Plate Name';
    const sourceWell = `A0${startSourceWell + index}`;
    const destinationWell = `B0${startDestinationeWell - index}`;
    const transferVolume = 100;

    const dummyData = [];
    dummyData.push(`"${plateName}"`);
    dummyData.push(`"${sourceWell}"`);
    dummyData.push(`"${plateName}"`);
    dummyData.push(`"${destinationWell}"`);
    dummyData.push(`"${transferVolume}"`);

    csv.push(dummyData.join(','));
  }

  return csv.join('\n');
};

// TODO: REMOVE!!!!!!!
/**
 * @deprecated
 */
const downloadPickListCsvTemplate = () => {
  const csvTemplate = generatePicklistCsvTemplate();

  const blop = new Blob([csvTemplate], {type: 'text/csv;charset=utf-8;'});
  const fileName = 'F.A.S.T. Pick List Template.csv';

  const url = window.URL.createObjectURL(new Blob([blop]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
};

// TODO: REMOVE!!!!!!!!!
/**
 * @deprecated
 */
export const showPickListModal = (openFileInput) => (dispatch) => {
  const content = (
    <div>
      <div style={{padding: 15, background: '#F0F0F0', borderRadius: '3px'}}>
        <img src="./svg/protocol-editor/pick-list-template.svg" alt="picklist-template" style={{width: '100%'}} />
      </div>
      <FormattedHTMLMessage id="modal.pickListTemplate.text" />
    </div>
  );
  const firstButtonText = (
    <div style={{display: 'flex'}}>
      <span className="icon-download" style={{fontSize: 18}} />
      &nbsp;
      {intl.formatMessage({id: 'modal.pickListTemplate.firstButton'})}
    </div>
  );
  const secondButtonText = (
    <div style={{display: 'flex'}}>
      <span className="icon-upload" style={{fontSize: 18}} />
      &nbsp;
      {intl.formatMessage({id: 'modal.pickListTemplate.secondButton'})}
    </div>
  );
  dispatch(
    showModal(
      intl.formatMessage({id: 'modal.pickListTemplate.title'}),
      content,
      [
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            downloadPickListCsvTemplate();
          },
          text: firstButtonText,
        },
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            openFileInput();
          },
          text: secondButtonText,
        },
      ],
      () => {
        dispatch(hideModal());
      },
      'auto-width outlined-btn-secondary',
      false,
      ModalSize.LARGE,
    ),
  );
};
